import { isConvexMutationTimeoutError } from '@brew/auth/convex-fetch-deadline'
import { isNextPrerenderAbandonedError } from '@brew/observability/core'
import { metricHttpMethod } from '@brew/observability/metrics'
import pRetry from 'p-retry'
import { getConvexAppError } from '@/lib/errors/app-error'
import { withDeadline } from '@/lib/shared/async/with-deadline'
import { isObjectLike } from '@/lib/shared/guards/is-object-like'
import {
  type ExternalRetryTelemetry,
  observeDependency,
} from './retry-observability'

const DEFAULT_ATTEMPTS = 3
const MIN_TIMEOUT_MS = 500
const MAX_TIMEOUT_MS = 5000
const MAX_PROVIDER_RETRY_DELAY_MS = 60_000
const PROVIDER_HINTS = [
  'anthropic',
  'apollo',
  'braze',
  'brevo',
  'browser-use',
  'clerk',
  'cohere',
  'convex',
  'customerio',
  'entri',
  'fal',
  'figma',
  'firecrawl',
  'gemini',
  'giphy',
  'google',
  'hubspot',
  'iterable',
  'klaviyo',
  'mailchimp',
  'mailjet',
  'mailgun',
  'mongodb',
  'openai',
  'onesignal',
  'postmark',
  'revenuecat',
  'resend',
  'sendgrid',
  'shopify',
  'stytch',
  'stripe',
  'supabase',
  'together',
  'turbopuffer',
  'twilio',
  'unsplash',
  'vercel',
  'workos',
] as const

type RetryPredicate = (error: unknown) => boolean

/**
 * An outage a write must outlast: while `isOutage(error)` holds, retrying
 * continues past `attempts` until `budgetMs` has passed since the first
 * attempt. Every other failure keeps the `attempts` budget.
 */
export type OutageRetryPolicy = {
  isOutage: RetryPredicate
  budgetMs: number
}

export type ExternalRetryOptions = {
  label?: string
  attempts?: number
  shouldRetry?: RetryPredicate
  signal?: AbortSignal
  /** Declares whether replaying the request can duplicate a side effect. */
  operation?: 'safe_read' | 'idempotent_write' | 'non_idempotent_write'
  /**
   * Per-attempt deadline. `fetchWithRetry` aborts the request at it (default
   * 15 s, `0` = none). `withExternalRetry` stops WAITING for an SDK call that
   * takes no signal: the abandoned call still settles in the background, so
   * set it on reads (`safe_read`). Either way the attempt rejects with a
   * `TimeoutError`, replayed only under `retryOnTimeout`.
   */
  timeoutMs?: number
  /**
   * Retry when OUR OWN per-attempt `timeoutMs` deadline fires (a `TimeoutError`
   * from `AbortSignal.timeout`). Off by default because a deadline usually means
   * the work is genuinely too slow to be worth repeating — but for a slow,
   * idempotent upstream (e.g. Figma's image renderer, which routinely stalls
   * past a fixed deadline and then succeeds) one more attempt is the difference
   * between a usable response and a spurious hard failure.
   *
   * A caller-initiated abort (via `options.signal`) is an `AbortError` and is
   * NEVER retried, regardless of this flag.
   */
  retryOnTimeout?: boolean
  /** Stable provider slug for trace filtering (for example `stripe`). */
  provider?: string
  /**
   * A provider header that paces a 429 retry when `Retry-After` is absent
   * (Mailgun documents `X-RateLimit-Reset` for this). Its value may be the
   * seconds to wait or the reset time as an epoch in seconds or milliseconds.
   */
  retryDelayHeader?: string
  /** Stable low-cardinality operation name; never include an id or URL. */
  operationName?: string
  /** Extra bounded metric tags and searchable correlation for this call. */
  telemetry?: ExternalRetryTelemetry
  /**
   * Outlast a provider outage instead of giving up after `attempts`. For a
   * terminal write that must land once the provider is back, on a path long
   * enough to wait for it. Convex: `CONVEX_OUTAGE_RETRY`.
   */
  outage?: OutageRetryPolicy
}

class RetryableResponseError extends Error {
  readonly response: Response
  readonly retryAfterMs?: number
  readonly status: number

  constructor(response: Response, label: string, retryAfterMs?: number) {
    super(`${label} failed with HTTP ${response.status}`)
    this.name = 'RetryableResponseError'
    this.response = response
    this.retryAfterMs = retryAfterMs
    this.status = response.status
  }
}

/** A reset header's delay: seconds to wait, or an epoch in s or ms. */
function parseResetHeaderDelayMs(value: string): number | undefined {
  const reset = Number(value)
  if (!Number.isFinite(reset) || reset < 0) {
    return
  }
  let delayMs = reset * 1000
  if (reset > 1e12) {
    delayMs = reset - Date.now()
  } else if (reset > 1e9) {
    delayMs = reset * 1000 - Date.now()
  }
  return Math.min(MAX_PROVIDER_RETRY_DELAY_MS, Math.max(0, Math.ceil(delayMs)))
}

function parseProviderRetryDelayMs(
  response: Response,
  retryDelayHeader?: string
): number | undefined {
  const rawValue =
    response.headers.get('retry-after') ??
    response.headers.get('x-sib-ratelimit-reset')
  const value = rawValue?.trim()
  if (!value) {
    const reset = retryDelayHeader
      ? response.headers.get(retryDelayHeader)?.trim()
      : undefined
    return reset ? parseResetHeaderDelayMs(reset) : undefined
  }

  const seconds = Number(value)
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.min(
      MAX_PROVIDER_RETRY_DELAY_MS,
      Math.max(0, Math.ceil(seconds * 1000))
    )
  }

  const timestamp = Date.parse(value)
  if (!Number.isFinite(timestamp)) {
    return
  }

  return Math.min(
    MAX_PROVIDER_RETRY_DELAY_MS,
    Math.max(0, timestamp - Date.now())
  )
}

function waitForRetryDelay(
  delayMs: number,
  signal: AbortSignal | undefined
): Promise<void> {
  if (delayMs <= 0) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timeoutId)
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'))
    }
    const timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, delayMs)

    if (signal?.aborted) {
      onAbort()
      return
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

function getStatusCode(error: unknown): number | undefined {
  if (error instanceof RetryableResponseError) {
    return error.status
  }

  if (!isObjectLike(error)) {
    return
  }

  const status = error.status
  if (typeof status === 'number') {
    return status
  }

  const statusCode = error.statusCode
  if (typeof statusCode === 'number') {
    return statusCode
  }
}

function isAbortError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false
  }

  return (
    error.name === 'AbortError' ||
    error.name === 'TimeoutError' ||
    error.message.toLowerCase().includes('aborted')
  )
}

/**
 * 4xx responses are by definition client errors — retrying with the same
 * payload won't change the outcome. The only common-sense exceptions are
 * 408 (Request Timeout) and 429 (Too Many Requests), both of which a retry
 * with backoff genuinely helps.
 *
 * 5xx responses are server-side and usually transient, so we retry all of
 * them by default.
 */
function isRetryableStatus(status: number): boolean {
  if (status >= 500) {
    return true
  }
  if (status === 408 || status === 429) {
    return true
  }
  // All other 4xx (400, 401, 403, 404, 405, 409, 410, 422, 423, …) are
  // permanent client errors. Retrying spams the API and doubles latency.
  if (status >= 400 && status < 500) {
    return false
  }
  // 3xx is a valid terminal answer, not a failure. Callers normally never
  // see one (fetch auto-follows), but the SSRF redirect-guarded wrapper
  // (`createRedirectGuardedFetch`) uses `redirect: 'manual'` and inspects
  // each hop itself — retrying a redirect response would just burn
  // attempts + backoff on every hop.
  if (status >= 300 && status < 400) {
    return false
  }
  return true
}

/**
 * Default retry predicate. Exported so call sites that want to layer
 * additional fast-fail checks (e.g. a config-error short-circuit) can
 * compose with the same abort / 4xx / non-retryable behavior used when
 * no custom `shouldRetry` is supplied.
 *
 * Returns `false` for: abort errors, Next's prerender-abandonment signal,
 * a Convex mutation that hit its fetch deadline, retryable=false app errors,
 * and non-retryable HTTP status codes (400, 401, 403, 404, 422, …).
 *
 * The prerender signal means the render that wanted the answer is gone; a
 * replay after backoff can only fail again, as `"use cache" called after
 * prerender ended`.
 */
export function shouldRetryByDefault(error: unknown): boolean {
  if (
    isAbortError(error) ||
    isNextPrerenderAbandonedError(error) ||
    isConvexMutationTimeoutError(error)
  ) {
    return false
  }

  const appError = getConvexAppError(error)
  if (appError?.retryable === false) {
    return false
  }

  const status = getStatusCode(error)
  if (typeof status === 'number') {
    return isRetryableStatus(status)
  }

  return true
}

function resolveAttempts(attempts?: number): number {
  if (typeof attempts !== 'number' || Number.isNaN(attempts)) {
    return DEFAULT_ATTEMPTS
  }

  return Math.max(1, Math.floor(attempts))
}

function inferProvider(label: string | undefined): string | undefined {
  const normalized = label?.toLowerCase().replaceAll('_', '-')
  return PROVIDER_HINTS.find((provider) => normalized?.includes(provider))
}

function stableOperationName(
  candidate: string | undefined,
  fallback: string
): string {
  const normalized = candidate?.trim()
  return normalized && /^[a-zA-Z][a-zA-Z0-9_.:-]{1,79}$/.test(normalized)
    ? normalized
    : fallback
}

function providerForFetch(input: Parameters<typeof fetch>[0]): string {
  try {
    const rawUrl =
      typeof input === 'string' || input instanceof URL
        ? String(input)
        : input.url
    const hostname = new URL(rawUrl).hostname.toLowerCase()
    return (
      PROVIDER_HINTS.find((provider) =>
        [provider, provider.replaceAll('-', '')].some((hint) =>
          hostname.includes(hint)
        )
      ) ?? 'external'
    )
  } catch {
    return 'external'
  }
}

/**
 * `AbortSignal.timeout` rejects with a `TimeoutError`; a caller-initiated abort
 * rejects with an `AbortError`. Only the former is ours to retry.
 */
function isTimeoutError(error: unknown): boolean {
  return error instanceof Error && error.name === 'TimeoutError'
}

function shouldRetryError(
  error: unknown,
  options: Pick<
    ExternalRetryOptions,
    'shouldRetry' | 'retryOnTimeout' | 'operation'
  > = {}
): boolean {
  // A Convex mutation whose answer never came may already have committed,
  // and replaying it can apply the write twice. Only a caller that declares
  // the write idempotent AND opts into timeout retries gets another attempt;
  // no custom predicate overrides that.
  if (isConvexMutationTimeoutError(error)) {
    return (
      options.operation === 'idempotent_write' &&
      options.retryOnTimeout === true
    )
  }

  if (options.retryOnTimeout && isTimeoutError(error)) {
    return true
  }

  if (options.shouldRetry) {
    return options.shouldRetry(error)
  }

  return shouldRetryByDefault(error)
}

/**
 * An SDK call takes no signal, so its deadline can only stop the WAIT: the
 * abandoned call settles in the background, observed and ignored. Rejects
 * like `AbortSignal.timeout`, so the timeout rules above apply.
 */
function withAttemptDeadline<T>(
  attempt: Promise<T>,
  timeoutMs: number,
  label: string
): Promise<T> {
  return withDeadline(attempt, timeoutMs, label, () => {
    const timeout = new Error(`${label} got no answer within ${timeoutMs} ms`)
    timeout.name = 'TimeoutError'
    return timeout
  })
}

export async function withExternalRetry<T>(
  fn: () => Promise<T>,
  options: ExternalRetryOptions = {}
): Promise<T> {
  return runExternalRetry(fn, options)
}

async function runExternalRetry<T>(
  fn: () => Promise<T>,
  options: ExternalRetryOptions,
  convex = false
): Promise<T> {
  const attempts = resolveAttempts(options.attempts)
  const provider = stableOperationName(
    options.provider ?? inferProvider(options.label),
    'external'
  )
  const operationName = stableOperationName(
    options.operationName ?? options.label,
    convex ? 'convex.action' : 'sdk_call'
  )
  const { outage } = options
  // A mutation timeout is never an outage to outlast (see `shouldRetryError`).
  const isOutage = (error: unknown) =>
    !isConvexMutationTimeoutError(error) && outage?.isOutage(error) === true
  const { timeoutMs } = options

  return observeDependency(
    {
      provider,
      operation: operationName,
      kind: options.operation,
      maxAttempts: attempts,
      outageBudgetMs: outage?.budgetMs,
      signal: options.signal,
      telemetry: options.telemetry,
      convex,
    },
    (observation) =>
      pRetry(
        () =>
          observation.attempt(() =>
            timeoutMs && timeoutMs > 0
              ? withAttemptDeadline(
                  fn(),
                  timeoutMs,
                  options.label ?? operationName
                )
              : fn()
          ),
        {
          // An outage is bounded by time, not by count (`maxRetryTime`).
          retries: outage ? Number.POSITIVE_INFINITY : attempts - 1,
          ...(outage ? { maxRetryTime: outage.budgetMs } : {}),
          factor: 2,
          minTimeout: MIN_TIMEOUT_MS,
          maxTimeout: MAX_TIMEOUT_MS,
          randomize: true,
          signal: options.signal,
          shouldRetry: async ({ error, attemptNumber: failedAttempt }) => {
            const shouldRetryAttempt =
              isOutage(error) ||
              (failedAttempt < attempts && shouldRetryError(error, options))
            if (!shouldRetryAttempt) {
              return false
            }
            observation.backoff()
            // p-retry calls this only after its count/time budget checks. An
            // onFailedAttempt hook also runs for terminal failures, whose
            // response must remain readable by the caller.
            if (error instanceof RetryableResponseError) {
              error.response.body?.cancel().catch(() => {
                // Already abandoning this attempt.
              })
              if (error.retryAfterMs !== undefined) {
                await waitForRetryDelay(error.retryAfterMs, options.signal)
              }
            }
            return true
          },
          onFailedAttempt: async ({
            error,
            attemptNumber: failedAttempt,
            retriesLeft,
          }) => {
            if (!(isOutage(error) || shouldRetryError(error, options))) {
              return
            }
            observation.failedAttempt(error, failedAttempt, retriesLeft)
          },
        }
      )
  )
}

export async function fetchWithRetry(
  input: Parameters<typeof fetch>[0],
  init?: Parameters<typeof fetch>[1],
  options: ExternalRetryOptions = {}
): Promise<Response> {
  const request = input instanceof Request ? input : undefined
  const method = (init?.method ?? request?.method ?? 'GET').toUpperCase()
  // RequestInit overrides the Request's signal, including an explicit null.
  // The caller's cancellation governs backoff as well as the active fetch.
  const signals = [
    init?.signal === undefined ? request?.signal : init.signal,
    options.signal,
  ].filter((signal): signal is AbortSignal => signal != null)
  const signal = signals.length > 0 ? AbortSignal.any(signals) : undefined
  const operation =
    options.operation ??
    (method === 'GET' || method === 'HEAD' || method === 'OPTIONS'
      ? 'safe_read'
      : 'non_idempotent_write')
  const retryOptions: ExternalRetryOptions = {
    ...options,
    signal,
    operation,
    provider: options.provider ?? providerForFetch(input),
    operationName: options.operationName ?? `http.${metricHttpMethod(method)}`,
    telemetry: {
      ...options.telemetry,
      tags: {
        ...options.telemetry?.tags,
        transport: 'http',
        method: metricHttpMethod(method),
      },
    },
    // The request's own signal enforces `timeoutMs` (and cancels the request).
    timeoutMs: undefined,
    attempts:
      operation === 'non_idempotent_write' && options.attempts === undefined
        ? 1
        : options.attempts,
  }

  try {
    const response = await withExternalRetry(async () => {
      const attemptSignals = signal ? [signal] : []
      if (options.timeoutMs !== 0) {
        attemptSignals.push(AbortSignal.timeout(options.timeoutMs ?? 15_000))
      }
      const requestInit = {
        ...init,
        ...(attemptSignals.length > 0
          ? {
              signal:
                attemptSignals.length === 1
                  ? attemptSignals[0]
                  : AbortSignal.any(attemptSignals),
            }
          : {}),
      }
      // Clone only the body fetch will consume. An init body replaces the
      // original, even when that original Request is already consumed.
      // One-shot requests retain native consumption behavior.
      const attemptInput =
        request &&
        init?.body == null &&
        (retryOptions.outage || resolveAttempts(retryOptions.attempts) > 1)
          ? request.clone()
          : input
      const response = await fetch(attemptInput, requestInit)

      if (!response.ok && isRetryableStatus(response.status)) {
        throw new RetryableResponseError(
          response,
          options.label ?? 'external fetch',
          operation === 'safe_read' && response.status === 429
            ? parseProviderRetryDelayMs(response, options.retryDelayHeader)
            : undefined
        )
      }

      return response
    }, retryOptions)

    return response
  } catch (error) {
    if (error instanceof RetryableResponseError) {
      return error.response
    }

    throw error
  }
}

export async function retryConvexAction<T>(
  fn: () => Promise<T>,
  options: ExternalRetryOptions = {}
): Promise<T> {
  return runExternalRetry(
    fn,
    {
      ...options,
      provider: 'convex',
      label: options.label ?? 'convex action',
      telemetry: {
        ...options.telemetry,
        tags: { ...options.telemetry?.tags, transport: 'convex' },
      },
    },
    true
  )
}
